import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'

export type ViewType = 'list' | 'board' | 'calendar' | 'gantt'
export type Density = 'simple' | 'detailed'
export type BoardGroupBy = 'status' | 'priority' | 'assignee'
export type ListGroupBy = 'status' | 'none'
export type BoardSwimlanes = 'none' | 'project'
export type GanttScale = 'Day' | 'Week' | 'Month' | 'Quarter' | 'Year'
export type DetailsPanel = 'off' | 'on'
export type SortBy = 'manual' | 'priority' | 'dueDate' | 'createdDate' | 'name'
export type SortDirection = 'asc' | 'desc'

// Defaults chosen to match the previously hard-coded directions in sortTasks,
// so existing users see no behavior change on first load.
export const defaultSortDirectionFor: Record<SortBy, SortDirection> = {
  manual: 'asc',
  priority: 'asc',
  dueDate: 'asc',
  createdDate: 'desc',
  name: 'asc',
}

export interface ProjectViewPreferences {
  viewType: ViewType
  density: Density
  visibleCustomFieldColumns: Array<string>  // ordered array of customFieldDefinitionIds
  boardGroupBy: BoardGroupBy
  /** List view grouping; older persisted prefs lack it, so read with a default. */
  listGroupBy?: ListGroupBy
  /** Board lanes (design 2b): one horizontal lane per project. */
  boardSwimlanes?: BoardSwimlanes
  ganttScale: GanttScale
  detailsPanel: DetailsPanel
  sortBy: SortBy
  sortDirection: SortDirection
}

interface CalendarNavState {
  /** Unix ms timestamp of the currently displayed date */
  currentDate: number
  /** Formatted label e.g. "March 2026" */
  label: string
}

interface ProjectViewPreferencesStateValues {
  // Map of projectId -> preferences
  preferences: Record<string, ProjectViewPreferences>
  // Map of projectId -> calendar navigation state (transient, not persisted)
  calendarNav: Record<string, CalendarNavState>
}

interface ProjectViewPreferencesState extends ProjectViewPreferencesStateValues {
  getPreferences: (projectId: string) => ProjectViewPreferences
  setViewType: (projectId: string, viewType: ViewType) => void
  setDensity: (projectId: string, density: Density) => void
  toggleDensity: (projectId: string) => void
  setVisibleCustomFieldColumns: (projectId: string, columns: Array<string>) => void
  toggleCustomFieldColumn: (projectId: string, fieldDefinitionId: string) => void
  setBoardGroupBy: (projectId: string, groupBy: BoardGroupBy) => void
  setListGroupBy: (projectId: string, groupBy: ListGroupBy) => void
  setBoardSwimlanes: (projectId: string, lanes: BoardSwimlanes) => void
  setGanttScale: (projectId: string, scale: GanttScale) => void
  setSortBy: (projectId: string, sortBy: SortBy) => void
  setSortDirection: (projectId: string, direction: SortDirection) => void
  toggleSortDirection: (projectId: string) => void
  setDetailsPanel: (projectId: string, value: DetailsPanel) => void
  toggleDetailsPanel: (projectId: string) => void
  setCalendarNav: (projectId: string, nav: CalendarNavState) => void
  getCalendarNav: (projectId: string) => CalendarNavState | null
  applyPreferences: (projectId: string, prefs: Partial<ProjectViewPreferences>) => void
}

export const defaultPreferences: ProjectViewPreferences = {
  viewType: 'list',
  density: 'simple',
  visibleCustomFieldColumns: [],
  boardGroupBy: 'status',
  listGroupBy: 'status',
  ganttScale: 'Week',
  detailsPanel: 'on',
  sortBy: 'manual',
  sortDirection: 'asc',
}

const initialState: ProjectViewPreferencesStateValues = {
  preferences: {},
  calendarNav: {},
}

const useProjectViewPreferencesStore = create<ProjectViewPreferencesState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        getPreferences: (projectId) => {
          const prefs = get().preferences[projectId]
          return { ...defaultPreferences, ...prefs }
        },
        setViewType: (projectId, viewType) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                viewType,
              },
            },
          }))
        },
        setDensity: (projectId, density) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                density,
              },
            },
          }))
        },
        toggleDensity: (projectId) => {
          const current = get().getPreferences(projectId)
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...current,
                density: current.density === 'simple' ? 'detailed' : 'simple',
              },
            },
          }))
        },
        setVisibleCustomFieldColumns: (projectId, columns) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                visibleCustomFieldColumns: columns,
              },
            },
          }))
        },
        toggleCustomFieldColumn: (projectId, fieldDefinitionId) => {
          const current = get().getPreferences(projectId)
          const columns = current.visibleCustomFieldColumns ?? []
          const isVisible = columns.includes(fieldDefinitionId)
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...current,
                visibleCustomFieldColumns: isVisible
                  ? columns.filter((id) => id !== fieldDefinitionId)
                  : [...columns, fieldDefinitionId],
              },
            },
          }))
        },
        setBoardGroupBy: (projectId, groupBy) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                boardGroupBy: groupBy,
              },
            },
          }))
        },
        setListGroupBy: (projectId, groupBy) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                listGroupBy: groupBy,
              },
            },
          }))
        },
        setBoardSwimlanes: (projectId, lanes) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                boardSwimlanes: lanes,
              },
            },
          }))
        },
        setGanttScale: (projectId, scale) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                ganttScale: scale,
              },
            },
          }))
        },
        setSortBy: (projectId, sortBy) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                sortBy,
                sortDirection: defaultSortDirectionFor[sortBy],
              },
            },
          }))
        },
        setSortDirection: (projectId, direction) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                sortDirection: direction,
              },
            },
          }))
        },
        toggleSortDirection: (projectId) => {
          const current = get().getPreferences(projectId)
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...current,
                sortDirection: current.sortDirection === 'asc' ? 'desc' : 'asc',
              },
            },
          }))
        },
        setDetailsPanel: (projectId, value) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...(state.preferences[projectId] ?? defaultPreferences),
                detailsPanel: value,
              },
            },
          }))
        },
        toggleDetailsPanel: (projectId) => {
          const current = get().getPreferences(projectId)
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...current,
                detailsPanel: current.detailsPanel === 'on' ? 'off' : 'on',
              },
            },
          }))
        },
        setCalendarNav: (projectId, nav) => {
          set((state) => ({
            calendarNav: {
              ...state.calendarNav,
              [projectId]: nav,
            },
          }))
        },
        getCalendarNav: (projectId) => {
          return get().calendarNav[projectId] ?? null
        },
        applyPreferences: (projectId, prefs) => {
          set((state) => ({
            preferences: {
              ...state.preferences,
              [projectId]: {
                ...defaultPreferences,
                ...state.preferences[projectId],
                ...prefs,
              },
            },
          }))
        },
      }),
      {
        name: 'moly-project-view-preferences',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ preferences: state.preferences }),
      },
    ),
  ),
)

export default useProjectViewPreferencesStore
